'use client'

import { useEffect, useRef, useSyncExternalStore } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { getLatestEvent } from 'src/utils/raceUtils'
import type { MarbleGame } from 'src/types/domain/Game'
import { crashLatchEngaged, CRASH_LATCH_UNKNOWN_ROUND } from './crashDisplayPhase'
import { resolveCrashEventInfo } from './crashEventInfo'
import { CRASH_RATE, CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'
import { crashEventClockOriginMs } from './crashHold'
import type { BeaconReading } from './useFrameBeacon'
import { BEACON_DEAD_MS, isBeaconFresh } from './crashFrameBeacon'

// Poll cadence while holding for the viewer's crash frame. Coarse on purpose:
// this drives phase flips (bars/pill), not a per-frame number.
const VISUAL_CRASH_POLL_MS = 100

// Race-relative time at which the crash frame occurs for multiplier M:
// the reveal hold plus the climb time ln(M)/rate.
const crashTimeFor = (m: number): number => CRASH_REVEAL_DELAY_SECONDS + Math.log(m) / CRASH_RATE

// ── Crash-hold state machine (bug b) ─────────────────────────────────────
// When the crash event arrives with a STALE beacon that was ALIVE earlier
// this round, wait up to this long for the beacon to return before falling
// back to an event-clock stamp. An immediate fallback painted "CRASHED
// 4.83x" on an empty street: the event clock ran ~4s ahead of the frames.
export const CRASH_HOLD_GRACE_MS = BEACON_DEAD_MS
// Hard cap: the total hold can never exceed the crash's event-clock moment
// (startTs + t_crash) by more than this — the UI must never hang un-crashed.
export const CRASH_HOLD_HARD_CAP_EXTRA_S = 8

export type CrashHoldDecision = Readonly<{
  /** true → land the crash visuals NOW; false → keep the live look (capped at M). */
  stamp: boolean
  /** Grace bookkeeping the caller must carry into its next call (per round). */
  graceStartMs: number | null
}>

/**
 * ONE decision for both crash surfaces (useVisualCrash's flip and
 * CrashMultiplierDisplay's phase machine): given the crash event is known,
 * should the crash visuals land on this tick?
 *
 *   - beacon fresh   → stamp exactly when the viewer's frame clock
 *                      (`t + age`) crosses t_crash; below it, hold. A fresh
 *                      reading also RESETS the grace (it measures continuous
 *                      beacon absence).
 *   - beacon null    → the beacon never lived this round (the lock is
 *                      round-scoped — see useFrameBeacon.setBeaconRound):
 *                      stamp on the event clock immediately.
 *   - beacon stale   → it lived this round and died: hold the live look for
 *                      up to CRASH_HOLD_GRACE_MS, stamping early only if a
 *                      returned reading crosses t_crash.
 *   - always         → hard-stamp past startTs + (t_crash + 8s); an invalid
 *                      multiplier is never holdable.
 */
export function resolveCrashHold(input: {
  nowMs: number
  startTsMs: number | undefined
  beacon: BeaconReading | null
  crashedMultiplier: number | undefined
  graceStartMs: number | null
}): CrashHoldDecision {
  const { nowMs, startTsMs, beacon, crashedMultiplier, graceStartMs } = input

  const mValid =
    typeof crashedMultiplier === 'number' &&
    Number.isFinite(crashedMultiplier) &&
    crashedMultiplier >= 1
  if (!mValid) return { stamp: true, graceStartMs }
  const tCrash = crashTimeFor(crashedMultiplier)

  // Hard cap — checked before everything else so no beacon state can hold
  // the UI un-crashed indefinitely.
  if (
    startTsMs !== undefined &&
    nowMs >= startTsMs + (tCrash + CRASH_HOLD_HARD_CAP_EXTRA_S) * 1000
  ) {
    return { stamp: true, graceStartMs }
  }

  const beaconFresh = isBeaconFresh(beacon)
  if (beaconFresh) {
    const frameClockT = beacon.t + beacon.ageMs / 1000
    return { stamp: frameClockT >= tCrash, graceStartMs: null }
  }

  // Never lived this round → nothing to wait for.
  if (beacon === null) return { stamp: true, graceStartMs }

  // Lived this round, lock went stale → grace window.
  const start = graceStartMs ?? nowMs
  return { stamp: nowMs - start >= CRASH_HOLD_GRACE_MS, graceStartMs: start }
}

/**
 * Store snapshot for read-only consumers (ShowRaceStatus pill, HUD overlay):
 *   - 'undriven' → no `useVisualCrash` driver mounted (spectator view, other
 *     games) — consumers MUST fall back to plain event-driven behaviour.
 *   - 'armed'    → a driver is live and the crash frame has NOT yet reached
 *     this viewer's screen (event may already be known) — hold crash visuals.
 *   - 'crashed'  → the crash is visible (or must be assumed visible) — land
 *     every crash visual now.
 */
export type VisualCrashStatus = 'undriven' | 'armed' | 'crashed'

// ── Module-level store ───────────────────────────────────────────────────
// The dock panel drives it (one driver per crash game screen); the status
// pill + HUD live in different subtrees, so a tiny module store beats
// threading context through the page.
let driverCount = 0
let crashedFlag = false
let snapshot: VisualCrashStatus = 'undriven'
const listeners = new Set<() => void>()

const computeStatus = (): VisualCrashStatus =>
  driverCount === 0 ? 'undriven' : crashedFlag ? 'crashed' : 'armed'

const publish = () => {
  const next = computeStatus()
  if (next === snapshot) return
  snapshot = next
  listeners.forEach((l) => l())
}

const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}
const getSnapshot = (): VisualCrashStatus => snapshot
const getServerSnapshot = (): VisualCrashStatus => 'undriven'

// ── Same-round crash latch (v9) ─────────────────────────────────────────
// The uuid the crashed flag was stamped for. Once set, ONLY a genuinely
// NEW race uuid may clear the flag: a same-uuid race_ready replay (event-
// list churn) or a transiently-undefined uuid (races[] churn) must never
// flip a SEEN crash back to armed/live. Shared helpers: crashDisplayPhase.
let crashedRoundId: string | null = null

const latchCrashedFlag = (roundId: string | undefined) => {
  crashedRoundId = roundId ?? CRASH_LATCH_UNKNOWN_ROUND
  if (crashedFlag) return
  crashedFlag = true
  publish()
}

const releaseCrashedFlag = (roundId: string | undefined) => {
  // Latched for this round (or churn noise) → the flag is untouchable.
  if (crashLatchEngaged(crashedRoundId, roundId)) return
  crashedRoundId = null
  if (!crashedFlag) return
  crashedFlag = false
  publish()
}

/**
 * Read-only view of the visual-crash store. Safe from any component; without
 * a mounted driver it reports 'undriven' (fall back to event-driven UI).
 */
export function useVisualCrashStatus(): VisualCrashStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/** Test-only: reset the module store between test cases. */
export function __resetVisualCrashStoreForTests(): void {
  driverCount = 0
  crashedFlag = false
  crashedRoundId = null
  snapshot = 'undriven'
  listeners.clear()
}

/**
 * THE visual-crash signal — one boolean the whole crash UI keys its crash
 * flip on (busted bars, status pill, HUD snap), so nothing lands seconds
 * before the crash is on the viewer's screen.
 *
 * `visualCrashed` flips true ONCE per round when the crash event is known
 * AND `resolveCrashHold` says the crash may land: the viewer's frame clock
 * reached the crash frame (`beaconT + age ≥ t_crash = 3 + ln(M)/CRASH_RATE`),
 * or the beacon never lived this round (immediate event-clock stamp), or a
 * mid-round-died beacon failed to return within the 5s grace — hard-capped
 * at t_crash-equivalent + 8s. Driven by a coarse ~100ms interval that starts
 * only once the crash event exists and self-clears after flipping. Resets
 * ONLY on a genuinely NEW round uuid (v9 latch): a same-uuid race_ready
 * replay or a transiently-undefined uuid (races[] churn) can never flip a
 * SEEN crash back to armed — crash never un-crashes in place.
 *
 * Also emits the once-per-round sync telemetry (`crash_sync_delta`) so the
 * engine's event-vs-frame offset can be tuned from real viewer data.
 */
export function useVisualCrash(
  game: MarbleGame,
  getBeaconTime: () => BeaconReading | null
): boolean {
  const race = game.races[0]
  const raceEvents = race?.raceEvents ?? []
  const roundId = race?.uuid
  const phase = getLatestEvent(raceEvents)?.eventType

  // ONE shared extraction (resolveCrashEventInfo — pure, tested): the first
  // USABLE multiplier across all crash events, so a payload-less
  // race_crashed can never mask race_ended's settled M.
  const { crashKnown, crashedMultiplier } = resolveCrashEventInfo(raceEvents)

  // The event clock's origin: race_started's stamp, moved back by 3 − R on a
  // short-hold round (crashHold.ts), so it reads the same clock as t_crash.
  const startTs = crashEventClockOriginMs(raceEvents)

  const status = useVisualCrashStatus()
  const visualCrashed = status === 'crashed'

  // Driver registration — flips the store out of 'undriven' while mounted.
  useEffect(() => {
    driverCount += 1
    publish()
    return () => {
      driverCount -= 1
      if (driverCount <= 0) {
        // Full teardown (no driver left) — the latch dies with the driver.
        crashedFlag = false
        crashedRoundId = null
      }
      publish()
    }
  }, [])

  // One telemetry sample per round (see below).
  const loggedDeltaRef = useRef(false)

  // Grace bookkeeping for resolveCrashHold — survives effect churn, reset
  // per round.
  const graceStartRef = useRef<number | null>(null)

  // Round reset: race_ready or a new round id re-arms the flip + telemetry.
  // The flag itself goes through the v9 latch: a same-uuid race_ready
  // replay or an undefined-uuid churn is a NO-OP on a seen crash — only a
  // genuinely new race uuid releases it (crash NEVER un-crashes in place).
  const prevRoundIdRef = useRef(roundId)
  useEffect(() => {
    const isNewRound = prevRoundIdRef.current !== roundId
    prevRoundIdRef.current = roundId
    if (!isNewRound && phase !== 'race_ready') return
    loggedDeltaRef.current = false
    graceStartRef.current = null
    releaseCrashedFlag(roundId)
  }, [roundId, phase])

  // The hold loop — exists only between "crash event known" and "crash frame
  // visible", then self-clears.
  useEffect(() => {
    if (!crashKnown || visualCrashed) return

    // Returns true once flipped (caller stops polling).
    const check = (): boolean => {
      const beacon = getBeaconTime()
      const beaconFresh = isBeaconFresh(beacon)

      // ── sync telemetry: event clock vs frame clock, once per round ──
      // delta > 0 → events run ahead of the viewer's frames (the usual case;
      // the engine offset can be tuned against the fleet median).
      if (!loggedDeltaRef.current && beaconFresh && startTs !== undefined) {
        loggedDeltaRef.current = true
        const beaconT = beacon.t + beacon.ageMs / 1000
        const eventClockT = (Date.now() - startTs) / 1000
        const delta = eventClockT - beaconT
        datadogLogs.logger.info('crash_sync_delta', { delta })
      }

      // ONE shared decision (bug b): stamp at the viewer's crash frame when
      // the beacon is live; grace-hold when it died mid-round (never stamp a
      // crash on footage that hasn't shown it); immediate event-clock stamp
      // only when the beacon never lived this round — hard-capped so the UI
      // can never hang un-crashed.
      const decision = resolveCrashHold({
        nowMs: Date.now(),
        startTsMs: startTs,
        beacon,
        crashedMultiplier,
        graceStartMs: graceStartRef.current
      })
      graceStartRef.current = decision.graceStartMs
      if (!decision.stamp) return false

      // Latch the flip to THIS round's uuid (v9): from here only a NEW
      // race uuid can take the crash visuals down again.
      latchCrashedFlag(roundId)
      return true
    }

    if (check()) return
    const id = setInterval(() => {
      if (check()) clearInterval(id)
    }, VISUAL_CRASH_POLL_MS)
    return () => clearInterval(id)
  }, [crashKnown, visualCrashed, crashedMultiplier, startTs, getBeaconTime, roundId])

  return visualCrashed
}
